import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { motion } from "framer-motion";
import {
  UserCircle, SlidersHorizontal, Archive, Key,
  ShieldStar, CreditCard, Buildings, TrashSimple, Lightning, Plugs
} from "@phosphor-icons/react";
import { ResourceBankManager } from "../components/ResourceBankManager";
import AdminTab from "../components/AdminTab";
import { ADMIN_EMAIL } from "../components/settings/shared";
import { PersonalizationTab } from "../components/settings/PersonalizationTab";
import { DefaultsTab } from "../components/settings/DefaultsTab";
import { ApiKeysTab } from "../components/settings/ApiKeysTab";
import { BillingTab } from "../components/settings/BillingTab";
import { WorkspaceTab } from "../components/settings/WorkspaceTab";
import { TrashTab } from "../components/settings/TrashTab";
import { IntegrationsTab } from "../components/settings/IntegrationsTab";

const TAB_ICONS = {
  personalization: UserCircle,
  defaults: SlidersHorizontal,
  resources: Archive,
  billing: CreditCard,
  apikeys: Key,
  integrations: Plugs,
  workspace: Buildings,
  trash: TrashSimple,
  admin: ShieldStar,
};

const BASE_TABS = [
  { id: "personalization", label: "Brand Voice" },
  { id: "defaults", label: "Defaults" },
  { id: "resources", label: "Resource Bank" },
  { id: "billing", label: "Billing" },
  { id: "apikeys", label: "API Keys" },
  { id: "integrations", label: "Integrations" },
  { id: "workspace", label: "Workspace" },
  { id: "trash", label: "Trash" },
];

export default function SettingsPage() {
  const { api, user, activeBusiness } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.email === ADMIN_EMAIL;
  const usesSystemKey = ["free_beta", "creator", "studio"].includes(user?.plan_tier || "free_beta") && !isAdmin;

  const buildTabs = () => {
    let tabs = [...BASE_TABS];
    if (usesSystemKey) {
      tabs = tabs.filter(t => t.id !== "apikeys");
    }
    if (isAdmin) {
      tabs.push({ id: "admin", label: "Admin" });
    }
    return tabs;
  };
  const TABS = buildTabs();

  const [tab, setTab] = useState(searchParams.get("tab") || "personalization");

  // Profile state — shared between Personalization and Defaults tabs
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      setLoadingProfile(true);
      const resp = await api.get("/profile");
      setProfile(resp.data);
    } catch {
      setProfile({});
    } finally {
      setLoadingProfile(false);
    }
  }, [api]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const updateField = (field, value) => setProfile((p) => ({ ...p, [field]: value }));

  const saveProfile = async () => {
    try {
      setSavingProfile(true);
      await api.post("/profile", profile);
      const { toast } = await import("sonner");
      toast.success("Profile saved!");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e.response?.data?.detail || "Failed to save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const toggleTone = (tone) => {
    const current = profile?.default_tone_variations || [];
    if (current.includes(tone)) {
      updateField("default_tone_variations", current.filter((t) => t !== tone));
    } else if (current.length < 3) {
      updateField("default_tone_variations", [...current, tone]);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="settings-page">
        <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight mb-6">Settings</h1>

        {/* Tabs */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-8 no-scrollbar">
          <div className="flex gap-1 bg-[var(--bg-card)] rounded-xl p-1 border border-[var(--border)] min-w-max sm:min-w-0" data-testid="settings-tabs">
            {TABS.map((t) => {
              const Icon = TAB_ICONS[t.id];
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    active ? "bg-[var(--accent-subtle)] text-[#FF4400] shadow-sm" : "text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
                  }`}
                >
                  <Icon size={18} weight={active ? "fill" : "regular"} />
                  <span className={active ? "" : "hidden sm:inline"}>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {tab === "personalization" && (
          <PersonalizationTab
            profile={profile}
            loadingProfile={loadingProfile}
            updateField={updateField}
            saveProfile={saveProfile}
            savingProfile={savingProfile}
            activeBusiness={activeBusiness}
          />
        )}

        {tab === "defaults" && (
          <DefaultsTab
            profile={profile}
            loadingProfile={loadingProfile}
            updateField={updateField}
            saveProfile={saveProfile}
            savingProfile={savingProfile}
            toggleTone={toggleTone}
          />
        )}

        {tab === "resources" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="resources-tab">
            <ResourceBankManager />
          </motion.div>
        )}

        {tab === "apikeys" && <ApiKeysTab />}

        {tab === "integrations" && <IntegrationsTab />}

        {tab === "billing" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="billing-tab">
            <BillingTab />
          </motion.div>
        )}

        {tab === "workspace" && <WorkspaceTab />}

        {tab === "trash" && <TrashTab />}

        {tab === "admin" && isAdmin && <AdminTab />}
      </main>
    </div>
  );
}
